import * as React from 'react';

import * as hiveCore from '@gitbook/hive-core';

import { useImportRuns } from '../../../Components';
import { useAdaptiveContextForSpace } from '../../Site';
import { DiffContext } from '../Diff';
import { NewPageMenuItem } from './NewPageMenuItem';
import { PagesRenderer, usePagesToRender } from './PagesRenderer';
import { PagesToCEmptyState } from './PagesToCEmptyState';
import { PageToCItem } from './PageToCItem';
import type { TableOfContentsProps } from './types';

/**
 * Table of contents for pages.
 */
export function PagesToC(props: TableOfContentsProps): React.ReactElement {
    const { readOnly, space, analyticsContext } = props;

    const { pages, revision, diffMode } = React.useContext(DiffContext);

    const rootImportRuns = useImportRuns({
        target: {
            space: space.id,
            changeRequest: props.changeRequest?.id ?? null,
            page: null,
        },
    });

    // Show hidden pages if the space is editable or if we are in diff mode or in any mode if the user has edit permissions.
    const showHiddenPages = !readOnly || Boolean(diffMode) || space.permissions.edit;

    const flatConflictedSubtree = React.useMemo(
        () => getFlatConflictedSubtrees(revision),
        [revision]
    );

    const pagesToRender = usePagesToRender({
        parent: { page: null, pages },
        showHiddenPages,
        importRuns: rootImportRuns,
    });

    const adaptiveContentContext = useAdaptiveContextForSpace(space);

    if (pagesToRender.groups.length === 0) {
        return <PagesToCEmptyState readOnly={readOnly} />;
    }

    return (
        <nav aria-label="Table of contents" className="py-4 pl-5">
            <PagesRenderer
                space={space}
                pages={pagesToRender}
                readOnly={readOnly}
                analyticsContext={analyticsContext}
                showHiddenPages={showHiddenPages}
                renderPage={(pageProps) => (
                    <PageToCItem
                        {...props}
                        {...pageProps}
                        key={pageProps.key}
                        flatConflictedSubtree={flatConflictedSubtree}
                        isParentHidden={false}
                        revision={revision}
                        adaptiveContentContext={adaptiveContentContext}
                    />
                )}
            />
            {readOnly ? null : <NewPageMenuItem />}
        </nav>
    );
}

function getFlatConflictedSubtrees(revision: hiveCore.Revision): hiveCore.RevisionPage[] {
    if (!revision.conflicts) {
        return [];
    }

    const deletedInBaseRoots = Object.entries(revision.conflicts.pages)
        .filter(
            ([, status]) =>
                status === hiveCore.RevisionConflictState.DeletedInHeadSubtreeModifiedInBase
        )
        .map(([id]) => id);

    const deletedInHeadRoots = Object.entries(revision.conflicts.pages)
        .filter(
            ([, status]) =>
                status === hiveCore.RevisionConflictState.DeletedInBaseSubtreeModifiedInHead
        )
        .map(([id]) => id);

    return [...deletedInBaseRoots, ...deletedInHeadRoots].flatMap((id) =>
        hiveCore.getPageDescendantsInRevision(revision, id)
    );
}
